/**
 * <TutorApplicationVideoQuestionPage> — une question vidéo de la candidature, avant et après la
 * caméra (page).
 *
 * L'ancien `InterviewScreen` en mode vidéo, qui pose `InterviewQuestion` dans le `FormStepper` :
 * la question en titre, puis selon la phase (`phase`) —
 *  - `thinking` : le champion de l'étape pose la question, en pied, dans sa bulle (`askedBy`), et
 *    l'en-tête dit l'étape ; le temps de réflexion qui court (« L'enregistrement vidéo va commencer
 *    dans 0 min 45 s ») et l'avertissement (quitter, c'est passer la question). Pour une question
 *    technique, le travail de l'élève est déjà ouvert en grand (`<TutorStudentWorkViewer>`) : la copie
 *    à côté au grand écran, dessous au téléphone, dans le cadre où on la retrouvera pendant
 *    l'enregistrement. « Commencer » saute la réflexion et ouvre la caméra, qui s'ouvre seule à zéro ;
 *  - `uploading` : la vidéo part, en mots et en barre ;
 *  - `recorded` : la vidéo enregistrée, qu'on revoit (le lecteur, posé par l'écran), et « Terminer » ;
 *  - `tooLate` : la question a été vue il y a plus de dix minutes sans réponse — « Pas de vidéo » et
 *    « Terminer ».
 * Une question déjà vue ne se repose pas (`alreadySeen`) : « passe à la suite », et « Continuer ».
 *
 * Quitter avant d'avoir répondu, c'est passer la question : la croix se confirme (`exitConfirm`),
 * et disparaît une fois la réponse donnée (l'ancien `removeExitButton`).
 *
 * Contrôlée de bout en bout.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import {
    type UITutorApplicationAsker,
    type UITutorVideoQuestion,
    type UITutorVideoQuestionPhase,
} from "../../../ports/primary/ui/types/tutorCandidature"
import { formatMinutesSeconds } from "../../../ports/primary/ui/formatters/dateFormatter"
import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { CameraPreviewFrame } from "../../molecules/CameraPreviewFrame"
import { Card } from "../../molecules/Card"
import { EmptyState } from "../../molecules/EmptyState"
import { Header } from "../../molecules/Header"
import {
    TutorApplicationChampionAsks,
    TutorApplicationChampionAsksSkeleton,
} from "../../molecules/TutorApplicationChampionAsks"
import {
    TutorStudentWorkViewer,
    WORK_SIDE_WIDTH,
    WORK_SPLIT_AT,
    type TutorStudentWorkPart,
} from "../../organisms/TutorStudentWorkViewer"
import { PageTemplate } from "../../templates/PageTemplate"
import {
    TutorApplicationExitDialog,
    type TutorApplicationExitConfirm,
} from "../TutorApplicationQuestionPage/TutorApplicationQuestionPage"

const TUTOR_APPLICATION_VIDEO_TEXTS = {
    countdownLead: "L'enregistrement vidéo va commencer dans",
    leaveWarning: "Attention, si tu quittes cet écran, c'est comme si tu avais passé la question.",
    start: "Commencer",
    uploading: "Envoi de la vidéo en cours…",
    uploaded: (percent: number) => `${percent} %`,
    finish: "Terminer",
    tooLate: "Pas de vidéo. Comme tu as déjà vu cette question, passe à la suite.",
    alreadySeen: "Tu as déjà vu cette question, passe à la suite.",
    next: "Continuer",
    loadingTitle: "Question vidéo",
}

const VIDEO_QUESTION_WIDTH = 640
/** Une question technique pendant la réflexion : la copie à gauche, le temps à droite. */
const WORK_WIDTH = 1120
/** La hauteur de la copie : sous le décompte au téléphone, à côté au grand écran. */
const WORK_HEIGHT = { narrow: 460, wide: 620 }

const noop = () => {}

/** La question telle qu'on la lit : une question technique dit la classe de la copie. */
export function videoQuestionTitle(question: UITutorVideoQuestion): string {
    return question.work != null
        ? `${question.question} (copie d'un élève en ${question.work.studentClass})`
        : question.question
}

export type TutorApplicationVideoQuestionPageProps = {
    /** `null` pendant le chargement de la question. */
    question: UITutorVideoQuestion | null
    loading?: boolean
    /** Le champion de l'étape, qui pose la question pendant la réflexion ; l'en-tête dit alors l'étape. */
    askedBy?: UITutorApplicationAsker
    phase: UITutorVideoQuestionPhase
    /** La question a déjà été vue : rien à poser. */
    alreadySeen?: boolean
    /** Le lecteur de la vidéo enregistrée (`recorded`), posé par l'écran. */
    player?: React.ReactNode
    onStart: () => void
    onFinish: () => void
    onContinue: () => void
    onClose: () => void
    /** La page du travail d'élève ouverte (question technique). Défaut `copy`. */
    workPart?: TutorStudentWorkPart
    onChangeWorkPart?: (part: TutorStudentWorkPart) => void
    /** « Veux-tu vraiment quitter ? », ouverte — la forme de toutes les pages de la candidature. */
    exitConfirm?: TutorApplicationExitConfirm
    testID?: string
}

export function TutorApplicationVideoQuestionPage(props: TutorApplicationVideoQuestionPageProps) {
    const {
        question,
        loading = false,
        askedBy,
        phase,
        alreadySeen = false,
        player,
        onStart,
        onFinish,
        onContinue,
        onClose,
        workPart = "copy",
        onChangeWorkPart,
        exitConfirm,
        testID,
    } = props
    const answered = phase.kind === "recorded" || phase.kind === "tooLate"
    // Pendant la réflexion, le champion pose la question : l'en-tête dit l'étape.
    const thinking = question != null && phase.kind === "thinking" && !alreadySeen
    const withWork = thinking && question.work != null
    const title =
        question == null
            ? TUTOR_APPLICATION_VIDEO_TEXTS.loadingTitle
            : thinking && askedBy != null
              ? askedBy.stepTitle
              : videoQuestionTitle(question)
    const header = (
        <Header title={title} steps={{ step: 1, total: 1, onClose: answered || alreadySeen ? undefined : onClose }} />
    )

    const footer = alreadySeen ? (
        <Button label={TUTOR_APPLICATION_VIDEO_TEXTS.next} fullWidth onPress={onContinue} />
    ) : phase.kind === "thinking" ? (
        <Button label={TUTOR_APPLICATION_VIDEO_TEXTS.start} fullWidth onPress={onStart} />
    ) : answered ? (
        <Button label={TUTOR_APPLICATION_VIDEO_TEXTS.finish} fullWidth onPress={onFinish} />
    ) : undefined

    return (
        <>
            <PageTemplate
                testID={testID}
                header={header}
                footer={loading ? undefined : footer}
                loading={loading}
                skeleton={<VideoQuestionSkeleton asked={askedBy != null} />}
                padding="4"
                maxContentWidth={withWork ? WORK_WIDTH : VIDEO_QUESTION_WIDTH}
            >
                {alreadySeen ? (
                    <EmptyState iconName="check-circle" title={TUTOR_APPLICATION_VIDEO_TEXTS.alreadySeen} />
                ) : question != null ? (
                    <Body
                        question={question}
                        askedBy={askedBy}
                        phase={phase}
                        player={player}
                        workPart={workPart}
                        onChangeWorkPart={onChangeWorkPart ?? noop}
                    />
                ) : null}
            </PageTemplate>
            {exitConfirm != null && <TutorApplicationExitDialog exitConfirm={exitConfirm} />}
        </>
    )
}

function Body(props: {
    question: UITutorVideoQuestion
    askedBy?: UITutorApplicationAsker
    phase: UITutorVideoQuestionPhase
    player?: React.ReactNode
    workPart: TutorStudentWorkPart
    onChangeWorkPart: (part: TutorStudentWorkPart) => void
}) {
    const { question, askedBy, phase, player, workPart, onChangeWorkPart } = props
    const { onLayout, atLeast: split } = useWidthAtLeast(WORK_SPLIT_AT)

    if (phase.kind === "uploading") {
        return (
            <View style={styles.center}>
                <Stack gap="3" align="center" style={styles.upload}>
                    <WaitingText>{TUTOR_APPLICATION_VIDEO_TEXTS.uploading}</WaitingText>
                    <View style={styles.bar}>
                        <ProgressBar value={phase.percent} color="role" trackColor="border" />
                    </View>
                    <Text size="text-lg" weight="bold" color="text-primary">
                        {TUTOR_APPLICATION_VIDEO_TEXTS.uploaded(phase.percent)}
                    </Text>
                </Stack>
            </View>
        )
    }

    if (phase.kind === "recorded") {
        return <CameraPreviewFrame>{player}</CameraPreviewFrame>
    }

    if (phase.kind === "tooLate") {
        return (
            <Text size="text-lg" color="text-primary" center>
                {TUTOR_APPLICATION_VIDEO_TEXTS.tooLate}
            </Text>
        )
    }

    const countdown = (
        <Stack gap="4">
            {askedBy != null && (
                <TutorApplicationChampionAsks champion={askedBy.champion} question={videoQuestionTitle(question)} />
            )}
            <Card p="5">
                <Stack gap="2" align="center">
                    <Text size="text-md" weight="semibold" color="text-muted" center>
                        {TUTOR_APPLICATION_VIDEO_TEXTS.countdownLead}
                    </Text>
                    <Text size="text-3xl" weight="extrabold" color="text-primary" accessibilityRole="timer">
                        {formatMinutesSeconds(Math.max(0, phase.secondsLeft))}
                    </Text>
                </Stack>
            </Card>
            <Text size="text-sm" weight="semibold" color="danger-strong" center>
                {TUTOR_APPLICATION_VIDEO_TEXTS.leaveWarning}
            </Text>
        </Stack>
    )

    if (question.work == null) return countdown

    // Le travail de l'élève ouvert en grand dès la réflexion : le même cadre que pendant l'enregistrement,
    // pour qu'on retrouve la copie où on l'a laissée.
    const viewer = <TutorStudentWorkViewer work={question.work} part={workPart} onChangePart={onChangeWorkPart} />
    // Un seul arbre : au seuil, la copie passe à gauche sans être remontée (ni son zoom, ni son image).
    return (
        <View onLayout={onLayout} style={styles.work(split)}>
            <View style={styles.workSide(split)}>{countdown}</View>
            <View style={styles.workCopy(split)}>{viewer}</View>
        </View>
    )
}

/**
 * La forme de la question qui se charge : le champion qui va la poser (son étape est connue avant la
 * question), la carte du temps de réflexion et l'avertissement.
 */
function VideoQuestionSkeleton(props: { asked: boolean }) {
    return (
        <Stack gap="4">
            {props.asked && <TutorApplicationChampionAsksSkeleton />}
            <Card p="5">
                <Stack gap="2" align="center">
                    <Skeleton textSize="text-md" width={260} />
                    <Skeleton textSize="text-3xl" width={140} />
                </Stack>
            </Card>
            <Skeleton textSize="text-sm" width="80%" style={styles.centered} />
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    center: { paddingVertical: theme.spacing["10"], alignItems: "center" },
    upload: { width: "100%" },
    bar: { width: "100%", maxWidth: 320 },
    centered: { alignSelf: "center" },
    // Au grand écran, la copie (second enfant) passe à gauche : la rangée est inversée.
    work: (split: boolean) => ({
        flexDirection: split ? "row-reverse" : "column",
        alignItems: split ? "flex-start" : "stretch",
        gap: split ? theme.spacing["6"] : theme.spacing["5"],
    }),
    workSide: (split: boolean) => ({ width: split ? WORK_SIDE_WIDTH : undefined }),
    workCopy: (split: boolean) => ({
        flex: split ? 1 : undefined,
        minWidth: 0,
        height: split ? WORK_HEIGHT.wide : WORK_HEIGHT.narrow,
    }),
}))
